// Dependency-free date + time picker built entirely from React Native
// primitives, so it renders identically on react-native-web AND
// react-native-macos (no `<input>`, no native-module / CocoaPods dependency —
// the community pickers don't span both targets). A month calendar grid plus
// hour / minute / AM-PM steppers.

import React, { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";
import { ChevronLeft, ChevronRight } from "lucide-react-native";
import { useColorScheme } from "../foundations/colorScheme";
import { tokens } from "../design-system/tokens";

export interface DateTimePickerProps {
  readonly value: Date;
  readonly onChange: (next: Date) => void;
  /** Earliest selectable instant; earlier days/times are disabled. */
  readonly minDate?: Date;
  readonly testID?: string;
}

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

/** 6×7 grid of dates covering the month `view` sits in (leading/trailing days). */
function monthGrid(view: Date): Date[] {
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay()); // back to Sunday
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}

export function DateTimePicker({
  value,
  onChange,
  minDate,
  testID,
}: DateTimePickerProps): React.ReactElement {
  const scheme = useColorScheme();
  const colorScheme: "light" | "dark" = scheme === "dark" ? "dark" : "light";
  const colors = tokens.color.semantic[colorScheme];

  // The month currently displayed (independent of the selected value).
  const [view, setView] = useState<Date>(
    () => new Date(value.getFullYear(), value.getMonth(), 1),
  );
  const cells = useMemo(() => monthGrid(view), [view]);
  const minDay = minDate !== undefined ? startOfDay(minDate) : null;

  const selectDay = (day: Date): void => {
    const next = new Date(value);
    next.setFullYear(day.getFullYear(), day.getMonth(), day.getDate());
    if (minDate !== undefined && next.getTime() < minDate.getTime()) {
      // Selecting the min day but an earlier time — clamp up to minDate.
      onChange(new Date(minDate));
      return;
    }
    onChange(next);
  };

  // Time parts.
  const hours24 = value.getHours();
  const hour12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minute = value.getMinutes();
  const isPm = hours24 >= 12;

  const applyTime = (h12: number, min: number, pm: boolean): void => {
    const h24 = (h12 % 12) + (pm ? 12 : 0);
    const next = new Date(value);
    next.setHours(h24, min, 0, 0);
    if (minDate !== undefined && next.getTime() < minDate.getTime()) {
      onChange(new Date(minDate));
      return;
    }
    onChange(next);
  };

  const chevron = colors.text.secondary;

  return (
    <View className="gap-3" testID={testID}>
      {/* Month header */}
      <View className="flex-row items-center justify-between px-1">
        <Pressable
          onPress={() =>
            setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))
          }
          accessibilityRole="button"
          accessibilityLabel="Previous month"
          className="w-8 h-8 items-center justify-center rounded-lg active:opacity-60"
          testID="dtp-prev-month"
        >
          <ChevronLeft size={18} strokeWidth={2} color={chevron} />
        </Pressable>
        <Text className="font-sans text-[15px] font-semibold text-light-text-primary dark:text-white">
          {MONTHS[view.getMonth()]} {view.getFullYear()}
        </Text>
        <Pressable
          onPress={() =>
            setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))
          }
          accessibilityRole="button"
          accessibilityLabel="Next month"
          className="w-8 h-8 items-center justify-center rounded-lg active:opacity-60"
          testID="dtp-next-month"
        >
          <ChevronRight size={18} strokeWidth={2} color={chevron} />
        </Pressable>
      </View>

      {/* Weekday header */}
      <View className="flex-row">
        {WEEKDAYS.map((w, i) => (
          <View key={i} className="flex-1 items-center py-1">
            <Text className="font-sans text-[11px] font-semibold text-light-text-secondary dark:text-[#8B8F97]">
              {w}
            </Text>
          </View>
        ))}
      </View>

      {/* Day grid (6 rows × 7) */}
      <View className="gap-1">
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <View key={row} className="flex-row">
            {cells.slice(row * 7, row * 7 + 7).map((day) => {
              const inMonth = day.getMonth() === view.getMonth();
              const selected =
                day.getFullYear() === value.getFullYear() &&
                day.getMonth() === value.getMonth() &&
                day.getDate() === value.getDate();
              const disabled =
                minDay !== null && startOfDay(day).getTime() < minDay.getTime();
              return (
                <Pressable
                  key={day.toISOString()}
                  disabled={disabled}
                  onPress={() => selectDay(day)}
                  accessibilityRole="button"
                  accessibilityState={{ selected, disabled }}
                  className={`
                    flex-1 aspect-square items-center justify-center rounded-full
                    ${selected ? "bg-light-accent-indigo dark:bg-[#537EFF]" : ""}
                    ${disabled ? "opacity-30" : "active:opacity-70"}
                  `}
                  testID={`dtp-day-${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}`}
                >
                  <Text
                    className={`font-sans text-[13px] ${
                      selected
                        ? "font-bold text-white"
                        : inMonth
                          ? "text-light-text-primary dark:text-[#E6E8EC]"
                          : "text-light-text-secondary dark:text-[#5A5E66]"
                    }`}
                  >
                    {day.getDate()}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ))}
      </View>

      {/* Time row */}
      <View className="flex-row items-center justify-center gap-2 pt-1">
        <Stepper
          label="Hour"
          value={hour12}
          onDec={() => applyTime(hour12 === 1 ? 12 : hour12 - 1, minute, isPm)}
          onInc={() => applyTime(hour12 === 12 ? 1 : hour12 + 1, minute, isPm)}
          display={String(hour12)}
          colors={colors}
        />
        <Text className="font-sans text-[18px] font-bold text-light-text-primary dark:text-white">
          :
        </Text>
        <Stepper
          label="Minute"
          value={minute}
          onDec={() => applyTime(hour12, (minute + 59) % 60, isPm)}
          onInc={() => applyTime(hour12, (minute + 1) % 60, isPm)}
          display={minute < 10 ? `0${minute}` : String(minute)}
          colors={colors}
        />
        <Pressable
          onPress={() => applyTime(hour12, minute, !isPm)}
          accessibilityRole="button"
          accessibilityLabel="Toggle AM/PM"
          className="ml-1 h-10 px-3 items-center justify-center rounded-lg bg-light-surface-secondary dark:bg-[#222429] active:opacity-70"
          testID="dtp-ampm"
        >
          <Text className="font-sans text-[14px] font-semibold text-light-text-primary dark:text-white">
            {isPm ? "PM" : "AM"}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

interface StepperProps {
  readonly label: string;
  readonly value: number;
  readonly display: string;
  readonly onInc: () => void;
  readonly onDec: () => void;
  readonly colors: { readonly text: { readonly secondary: string } };
}

function Stepper({
  label,
  display,
  onInc,
  onDec,
  colors,
}: StepperProps): React.ReactElement {
  return (
    <View className="flex-row items-center rounded-lg bg-light-surface-secondary dark:bg-[#222429]">
      <Pressable
        onPress={onDec}
        accessibilityRole="button"
        accessibilityLabel={`Decrease ${label}`}
        className="w-8 h-10 items-center justify-center active:opacity-60"
      >
        <ChevronLeft size={16} strokeWidth={2} color={colors.text.secondary} />
      </Pressable>
      <Text className="min-w-[26px] text-center font-sans text-[16px] font-semibold text-light-text-primary dark:text-white">
        {display}
      </Text>
      <Pressable
        onPress={onInc}
        accessibilityRole="button"
        accessibilityLabel={`Increase ${label}`}
        className="w-8 h-10 items-center justify-center active:opacity-60"
      >
        <ChevronRight size={16} strokeWidth={2} color={colors.text.secondary} />
      </Pressable>
    </View>
  );
}
